/* Social, on the Momentum pattern (docs/social-redesign-spec.md, phase A1).
 *
 * What only a social post knows: its platforms, where each one folds the text
 * ("…see more"), and the three words every Momentum dot uses. Pure, so the
 * table, the rail and the preview all read the same answer (CLAUDE.md rule 6:
 * one helper per record type, so two lists can never disagree).
 */

/* The fold is approximate and FROM GENERAL KNOWLEDGE (spec, "What the platforms
   are doing", 2026-09): LinkedIn shows about the first three lines or ~210
   characters before "…see more"; Instagram shows the picture, then about 125
   characters. Facebook shares LinkedIn's until there is a reason not to (spec
   decision 9). Worth checking against the platforms' help pages before anyone
   treats it as exact — the preview is a guide to where the hook has to land,
   not a promise. */
export const SOCIAL_PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", limit: 3000, foldChars: 210, foldLines: 3, pictureFirst: false },
  { id: "instagram", label: "Instagram", limit: 2200, foldChars: 125, foldLines: 2, pictureFirst: true },
  { id: "facebook", label: "Facebook", limit: 2000, foldChars: 210, foldLines: 3, pictureFirst: false },
];

export const platformOf = (id) => SOCIAL_PLATFORMS.find((p) => p.id === id) || SOCIAL_PLATFORMS[0];

export const postWords = (post) => String(post?.caption || "").trim().split(/\s+/).filter(Boolean).length;

/* THE SAME THREE WORDS AS pageState AND moduleStatus (rule 6, settled
   2026-09-15): empty, draft, done — and Done is HER mark, never inferred from
   having words. Posts saved before 2026-09-24 hold "saved" or "draft"; both
   read as Draft until she marks one, which is the honest answer — nothing ever
   asked her whether they were finished. */
export function postState(post) {
  if (!String(post?.caption || "").trim()) return "empty";
  return post?.status === "done" ? "done" : "draft";
}
export const POST_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

/**
 * Where the platform cuts the post off. `shown` is what reads before "…see
 * more"; `folded` is whether anything is hidden. Cut at whichever comes first,
 * the line count or the character count, and never mid-word.
 */
export function previewFold(caption, platformId) {
  const p = platformOf(platformId);
  const text = String(caption || "");
  const lines = text.split("\n");
  let byLines = lines.slice(0, p.foldLines).join("\n");
  let shown = byLines.length < text.length ? byLines : text;
  if (shown.length > p.foldChars) {
    const cut = shown.slice(0, p.foldChars);
    const space = cut.lastIndexOf(" ");
    shown = (space > p.foldChars * 0.6 ? cut.slice(0, space) : cut).trimEnd();
  }
  return { shown, folded: shown.length < text.trimEnd().length };
}

/* What a post needs before it is written at all. A row with nothing in it would
   sit in the table as "Untitled post" and in her post history as nothing; the
   footer says so in amber instead of staying silent (rule 1). */
export const postHasWords = (post) =>
  !!(String(post?.caption || "").trim() || String(post?.firstComment || "").trim() || String(post?.title || "").trim());


/* ── The Brill button (spec phase A2) ────────────────────────────────────────
   ONE button whose menu depends on the box (spec section 3). Her list,
   2026-09-25: Tighten, Sound more like me, Sharpen the opening, Fit for
   {platform}, plus Fix spelling and grammar, and the quote card KEPT as a menu
   item. "Work in the picture" appears only when a picture is attached. Pure, so
   what the menu offers is pinned by a test rather than by clicking. */
export function brillMenu({ caption, hasPicture, platformLabel }) {
  /* An EMPTY box: her three, in her order (2026-10-01). "Pick from library"
     puts her own words in as they are — no Brill, no cost — and the one-taps
     below are then how she cleans them up. "Draft it for me" stays the one
     where a Library pick is context and Brill writes the post. */
  if (!String(caption || "").trim()) {
    return [
      { id: "ask", label: "Ask Brill…", desc: "Open the chat." },
      { id: "draft", label: "Draft it for me…", desc: "Say what it's about or start from an idea; pick pillar, theme, hook and ask, or let Brill." },
      { id: "library", label: "Pick from library…", desc: "Your own idea or story, word for word. Then Tighten or Fix spelling if you like." },
    ];
  }
  return [
    { id: "tighten", label: "Tighten", desc: "Same post, fewer words." },
    { id: "voice", label: "Sound more like me", desc: "Rewritten against your Voice rules." },
    { id: "opening", label: "Sharpen the opening", desc: "Just the lines above \"…see more\"." },
    ...(hasPicture ? [{ id: "picture", label: "Work in the picture", desc: "Ties the words to the picture you attached." }] : []),
    { id: "fit", label: `Fit for ${platformLabel}`, desc: `Length and shape for how ${platformLabel} shows it.` },
    { id: "spelling", label: "Fix spelling and grammar", desc: "Nothing else changes." },
    { id: "quote", label: "Make a quote card", desc: "Pulls the strongest line into a card brief." },
    { id: "redraft", label: "Redraft from scratch…", desc: "A new post — pick the pillar and hook style again. Yours stays until you choose." },
    { id: "ask", label: "Ask Brill…", desc: "Open the chat." },
  ];
}

/* What each one-tap SAYS to Brill. They ride the existing refine call as her
   message, so a tap costs what one chat turn costs (~$0.02) and reads her Voice
   the same way. Every one names what must NOT change, because the refine
   guide's worst failure is a different post on the same topic. */
export function tapAsk(id, { platformLabel, limit, about } = {}) {
  switch (id) {
    case "tighten": return "Tighten this post. Cut every word that isn't earning its place, keep my points, my opening and my ask. Shorter, not different.";
    case "voice": return "Make this sound more like me. Hold it against my voice and writing rules and fix what doesn't sound like me. Keep what it says.";
    case "opening": return `Sharpen the opening — only the first line or two, the part ${platformLabel} shows before "…see more". Leave the rest of the post exactly as it is.`;
    /* A FILTER, not a new subject (her call, 2026-09-28: it was "overwriting
       the context and ideas"). */
    case "picture": return `I'm posting this with a picture: ${about}. Work the picture in as a detail or a way into the opening, so the words and the picture belong together. The post stays about what it's about now: keep its subject, its idea, its story and its point, and change only as much as that takes. The picture is never the topic.`;
    case "fit": return `Fit this for ${platformLabel}: the length, line breaks and shape that work there${limit ? `, well under ${limit} characters` : ""}. Keep what it says.`;
    case "spelling": return "Fix spelling, grammar and punctuation only. Change nothing else — not a word choice, not a line break.";
    default: return null;
  }
}


/* ── The suggestion card's diff ─────────────────────────────────────────────
   A one-tap result waits in a card under the box, struck and highlighted words
   (spec decision 6), and never writes itself into her post (rule 4). Word-level
   LCS: a post is at most a few hundred words, so the table is small. Whitespace
   is kept as its own token, so line breaks survive into the card. */
export function wordDiff(before, after) {
  const tok = (s) => String(s || "").split(/(\s+)/).filter((t) => t !== "");
  const a = tok(before), b = tok(after);
  const n = a.length, m = b.length;
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text; else out.push({ type, text });
  };
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) { push("cut", a[i]); i++; }
    else { push("add", b[j]); j++; }
  }
  while (i < n) push("cut", a[i++]);
  while (j < m) push("add", b[j++]);
  return out;
}


/* ── The picture and the quote card share `visual` ──────────────────────────
   `visual` is one jsonb column (0001). The old screen put a quote card BRIEF
   there as `{ type: "quote", … }`; a real picture is `{ type: "photo", url,
   about, alt }` (spec). She kept the quote card (2026-09-25), so a post can
   hold BOTH — a picture she attached and a brief she may turn into one. The
   picture leads and the brief rides under `quote`; attaching a picture never
   throws a brief away, and removing one hands the brief back its old shape. */
const QUOTE_KEYS = ["pull_quote", "background_hex", "text_hex", "accent_hex", "reason", "mood", "template", "layout_note"];

export function quoteOf(visual) {
  if (!visual) return null;
  if (visual.type === "quote") return visual;
  return visual.quote ? { type: "quote", ...visual.quote } : null;
}
export const pictureOf = (visual) => (visual?.type === "photo" && visual.url ? visual : null);

const quoteBody = (q) => Object.fromEntries(QUOTE_KEYS.filter((k) => q?.[k] != null).map((k) => [k, q[k]]));

export function withPicture(visual, pic) {
  const q = quoteOf(visual);
  const prev = pictureOf(visual);
  /* A different picture is a different "what's in it" — only the same one keeps
     what she wrote about it. */
  const same = prev && prev.url === pic.url;
  const next = { type: "photo", url: pic.url, about: pic.about ?? (same ? prev.about : "") ?? "", alt: pic.alt ?? (same ? prev.alt : "") ?? "" };
  return q ? { ...next, quote: quoteBody(q) } : next;
}
export function withoutPicture(visual) {
  const q = quoteOf(visual);
  return q ? { type: "quote", ...quoteBody(q) } : null;
}
export function withQuote(visual, brief) {
  const pic = pictureOf(visual);
  return pic ? { ...pic, quote: quoteBody(brief) } : { type: "quote", ...quoteBody(brief) };
}


/* ── Length (her ask, 2026-09-25) ────────────────────────────────────────────
   Short / Medium / Long with the range shown, not a slider: Brill writes to a
   shape, and three buttons are one tap on a phone. Mirrored in
   api/_lib/prompts.js (SOCIAL_LENGTHS) — socialPostLength.test.js pins both.
   Instagram defaults Short: the caption sits under a picture and folds at
   about 125 characters. */
export const POST_LENGTHS = [
  { id: "short", label: "Short", words: "60–120 words" },
  { id: "medium", label: "Medium", words: "150–250 words" },
  { id: "long", label: "Long", words: "300–450 words" },
];
export const defaultLength = (platformId) => (platformId === "instagram" ? "short" : "medium");


/* ── The ask, and the link it needs ──────────────────────────────────────────
   Her model, 2026-09-25: the OFFER is what a post is about; the ASK is what it
   invites — nothing, a message, learn more about the offer, or book a call on
   one of her meeting links. Both can be set at once (a post about her offer
   that asks for a strategy call). One value for the ask, as the dropdown
   holds it: "none" | "message" | "learn" | "link:<ctaId>". */
export function askOf(post) {
  const kind = post?.inputs?.cta;
  if (kind === "library" && post?.ctaId) return `link:${post.ctaId}`;
  if (kind === "message") return "message";
  /* "offer" is the old screen's generic "Invite to my offer" — with an offer
     picked it IS "learn more about it"; without one there is nothing to learn. */
  if ((kind === "learn" || kind === "offer") && post?.offerId) return "learn";
  return "none";
}

/* The one link the ask needs, for the FIRST COMMENT (links go in the comments,
   never the post). A meeting link's own URL; for "learn", the offer's meeting
   link or pasted link. A Sites page is a handoff document with no live address
   (spec), so an offer that only points at a page has no link to give — null,
   and the comment is left alone. */
export function askLink(ask, { offer = null, ctas = [] } = {}) {
  const live = (u) => (/^https?:\/\/\S+$/i.test(String(u || "").trim()) ? String(u).trim() : null);
  if (String(ask || "").startsWith("link:")) {
    return live(ctas.find((c) => c.id === ask.slice(5))?.link);
  }
  if (ask === "learn" && offer) {
    if (offer.ctaId) return live(ctas.find((c) => c.id === offer.ctaId)?.link);
    return live(offer.linkUrl);
  }
  return null;
}


/* ── THE POST'S DAY (her calls, 2026-10-06) ────────────────────────────────────
   "Post date would be more important" than when it was last edited — so the
   table shows the day a post is FOR and sorts by it. A single post can now
   carry one too, and a send to GHL always has one: "if it pushed to GHL it has
   a date." `scheduledDate` (social_posts.scheduled_date, timestamptz) holds
   the DAY as YYYY-MM-DD, as an arc's posts already do; the time she sends at
   lives in the send, because only her browser knows what 9am means. */
export const postDay = (post) => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(post?.scheduledDate || ""));
  return m ? m[1] : "";
};

/** Soonest first; posts with no day after them, newest edited first. */
export function sortPostsByDate(posts = []) {
  const stamp = (p) => Date.parse(p?.updatedAt || p?.createdAt || "") || 0;
  return [...(posts || [])].sort((a, b) => {
    const da = postDay(a), db = postDay(b);
    if (da && db) return da < db ? -1 : da > db ? 1 : stamp(b) - stamp(a);
    if (da || db) return da ? -1 : 1;
    return stamp(b) - stamp(a);
  });
}

/** The moment a send is for, in her time, as ISO — or why it can't be. GHL
 *  drops a moment already past (ghlSocial scheduleFor), which would file the
 *  draft on "now", so the pop-up says so instead of sending it. */
export function sendMoment(day, time = "09:00", now = new Date()) {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ""));
  const t = /^(\d{1,2}):(\d{2})$/.exec(String(time || ""));
  if (!d) return { error: "Pick the day it goes out." };
  if (!t) return { error: "Pick a time." };
  const at = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  if (at.getTime() < now.getTime() + 5 * 60000) return { error: "That's already passed — pick a time ahead." };
  return { iso: at.toISOString() };
}


/* ── THE CALENDAR (her read, 2026-10-06: Content Plan is "just a calendar
   view of your posts") ─────────────────────────────────────────────────────
   A month as weeks of YYYY-MM-DD days, Sunday first, padded with the days of
   the months either side so every week is whole. */
export function monthGrid(year, month) {
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const last = new Date(year, month + 1, 0);
  const weeks = [];
  for (let d = new Date(start); weeks.length < 6; ) {
    const week = [];
    for (let i = 0; i < 7; i++) { week.push(iso(d)); d.setDate(d.getDate() + 1); }
    weeks.push(week);
    if (d > last) break;
  }
  return weeks;
}

/** Posts by the day they are for; the ones with no day under "". */
export function postsByDay(posts = []) {
  const out = {};
  for (const p of sortPostsByDate(posts)) (out[postDay(p)] ||= []).push(p);
  return out;
}


/* ── THE POSTS TABLE: filter and sort (her ask, 2026-10-09) ──────────────────
   "Ideally make the top filter by platform or pillar or status but default
   to most recent." Most recent = the post she last WORKED ON (updated, else
   created), newest first — the soonest-date order of 2026-10-06 opened on a
   day already past. The same shape as Admin → Users: filters above, any
   heading sorts, again to reverse.
   DATE is not a plain date sort, her read the same day ("it starts 10/7 but
   that's past"): COMING UP first — today onward, soonest first — then the
   days gone, most recent first. Reversed, it is simply the latest day first.
   A post with no day is last either way; so is a post with no pillar. */
export const POST_SORTS = ["recent", "title", "platform", "date", "pillar", "words", "status"];
const STATE_ORDER = { empty: 0, draft: 1, done: 2 };
const workedOn = (p) => Date.parse(p?.updatedAt || p?.createdAt || "") || 0;

export function postsView(posts = [], { platform = "all", pillar = "all", status = "all", sort = { key: "recent", dir: "desc" } } = {}, today = null) {
  const now = today || new Date().toISOString().slice(0, 10);
  const shown = (posts || [])
    .filter((p) => platform === "all" || platformOf(p.platform).id === platform)
    .filter((p) => pillar === "all" || (pillar === "" ? !p.inputs?.pillar : p.inputs?.pillar === pillar))
    .filter((p) => status === "all" || postState(p) === status);
  const asc = sort?.dir === "asc";
  const flip = (c) => (asc ? c : -c);
  const cmp = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
  const tie = (a, b) => workedOn(b) - workedOn(a);
  return shown.slice().sort((a, b) => {
    switch (sort?.key) {
      case "title": return flip(cmp(String(a.title || "").toLowerCase(), String(b.title || "").toLowerCase())) || tie(a, b);
      case "platform": return flip(cmp(platformOf(a.platform).label, platformOf(b.platform).label)) || tie(a, b);
      case "words": return flip(postWords(a) - postWords(b)) || tie(a, b);
      case "status": return flip(STATE_ORDER[postState(a)] - STATE_ORDER[postState(b)]) || tie(a, b);
      case "pillar": {
        const x = a.inputs?.pillar || "", y = b.inputs?.pillar || "";
        if (!x !== !y) return x ? -1 : 1;
        return flip(cmp(x.toLowerCase(), y.toLowerCase())) || tie(a, b);
      }
      case "date": {
        const x = postDay(a), y = postDay(b);
        if (!x !== !y) return x ? -1 : 1;
        if (!x) return tie(a, b);
        if (!asc) return cmp(y, x) || tie(a, b);
        const ax = x >= now, ay = y >= now;
        if (ax !== ay) return ax ? -1 : 1;
        return (ax ? cmp(x, y) : cmp(y, x)) || tie(a, b);
      }
      default: return flip(workedOn(a) - workedOn(b));
    }
  });
}

/** Her pillars as the table can filter them: the ones her posts carry. */
export const postPillars = (posts = []) => [...new Set((posts || []).map((p) => p.inputs?.pillar).filter(Boolean))].sort((a, b) => a.localeCompare(b));
